import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Slider, InputAdornment, TextField } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { InputMaskWeight } from '../../Helpers/Formatters';
import { useController, useFormContext } from 'react-hook-form';
import './SliderInput.scss';

// Keep the field display aligned with the mask while preserving the stored "pounds" value.
const toDisplayWeight = (weightValue = '') => weightValue?.replace('pounds', 'lbs') || '';

// Memoize the wrapper so unrelated form state changes do not repaint this slider/input pair.
const WeightInput = memo((props) => {
    const { variant, name, id, label, value, onChange, min, max, className, openEditPopUp } = props;
    const formContext = useFormContext();
    // Use the parent FormProvider control; creating a fallback form during render adds avoidable hook work.
    const { control } = formContext;
    const {
        field,       // Contains: onChange, onBlur, value, ref
        fieldState: { error }   // Contains: error
    } = useController({ name, control });
    const [weight, setWeight] = useState(() => toDisplayWeight(value));

    // Keep the local masked value in sync when the parent resets or hydrates the form.
    useEffect(() => {
        setWeight(toDisplayWeight(value));
    }, [value]);

    const handleSliderChange = useCallback((event, newValue) => {
        const paddedWeight = newValue.toString().padStart(3, '0');
        const displayWeight = `${paddedWeight} lbs`;
        const formWeight = `${paddedWeight} pounds`;
        setWeight(displayWeight);
        field.onChange(formWeight);
        onChange(formWeight);
    }, [field, onChange]);

    const weightValue = useMemo(() => parseInt((weight || '').split(' ')[0], 10) || 0, [weight]);
    // Render only endpoint marks; step=1 still supports every pound without creating 600+ mark nodes.
    const weightMarks = useMemo(() => [
        { value: min, label: `${min}lbs` },
        { value: max, label: `${max}lbs` },
    ], [max, min]);

    // Stable adornment and slot props avoid remounting the masked input on parent renders.
    const endAdornment = useMemo(() => (
        <InputAdornment position="end" className="text-input-carrot" disablePointerEvents>
            <ArrowDropDownIcon />
        </InputAdornment>
    ), []);

    const slotProps = useMemo(() => ({
        input: {
            inputComponent: InputMaskWeight,
            endAdornment,
        },
        inputLabel: {
            shrink: weight ? true : undefined,
        },
    }), [endAdornment, weight]);

    const handleTextChange = useCallback((e) => {
        // Update local display and RHF/parent state in one lightweight change handler.
        const newValue = e.target.value;
        setWeight(toDisplayWeight(newValue));
        field.onChange(newValue);
        onChange && onChange(newValue);
    }, [field, onChange]);

    return (
        <div className='slider-input-container'>
            <TextField
                variant={variant || 'outlined'}
                name={name}
                id={id}
                label={label}
                value={weight}
                helperText={error?.message}
                error={!!error || (openEditPopUp && !weight)}
                className={className}
                onChange={handleTextChange}
                slotProps={slotProps} />
            <div className="slider-container">
                <div className="me-2 slider-div">
                    <Slider
                        min={min}
                        max={max}
                        // Keep one-pound precision without forcing MUI to render a mark for every value.
                        step={1}
                        value={weightValue}
                        onChange={handleSliderChange}
                        className="m-2"
                        sx={{ mb: '10px' }}
                        marks={weightMarks}
                        tabIndex={-1}
                    />
                </div>
            </div>
        </div>
    );
});

WeightInput.propTypes = {
    variant: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    className: PropTypes.string,
    openEditPopUp: PropTypes.bool,
};

export default WeightInput;
